import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { messageHandler } from "utils/messageHandler";
import { AGENTOFFICEPRODUCTADDEDIT_MESSAGES } from "utils/messages";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Brand, Category, Product, Agent, Media } from "../../types";
import { nameValidation, statusValidation } from "validationSchemas/common";
import SharedEditProductUI from "components/SignageOfficeAgentInventoryProduct";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    type ColumnDef,
    type Table,
} from "@tanstack/react-table";
import { UIReactSelect, UIInput } from "components/WarehouseInputfields";
import { getImageSrc, useVariantImageUrlMap, buildLegacyUrl } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles, } from "components/Imageshelper";
import { AsyncPaginate, type LoadOptions } from "react-select-async-paginate";
import type { GroupBase } from "react-select";
import { fetchBrandsByUserSelect2, fetchCategoriesByUserSelect2 } from "api/postTypeApiCalls";
import { useBrandCategorySelector } from "components/BrandCategorySelector";
import { getAuthCookie } from "../../utils/authCookies";
// type LinkedInstaller = {
//     id: string;
//     name: string;
//     warehouse_info: WarehouseInfo;
// }

// type WarehouseInfo = {
//   id: string;
//   label: string;
//   bin_location: string;
//   linked_product_quantity: number;
// }
type SelectOption = { value: string; label: string };

// Bin Location Option type for Select2 API
type BinLocationOption = {
    value: string; // Combined identifier or bin_id
    label: string; // Display: "Warehouse > Zone > Rack > Bin"
    warehouse_id: string;
    warehouse_name: string;
    zone_id: string;
    zone_name: string;
    rack_id: string;
    rack_name: string;
    bin_id: string;
    bin_name: string;
    max_volume?: number;
    used_volume?: number;
};

interface Installer {
    _id: string;
    supplier_id: string;
    name: string;
    email: string;
}

// interface FirmDetails {
//   companyID: string;
//   companyName: string;
// }

type ProductObj = {
    product_id: string;
    agent_name: string;
    signage_company_id: string;
    linked_installer_details: any
    product: Product;
    created_by: {
        id: string;
        name: string;
    };
};

type WHRow = {
    warehouse_id: string;
    warehouse_name: string;
    zone_id: string; zone_name: string;
    rack_id: string; rack_name: string;
    bin_id: string; bin_name: string;
    available: number; on_hand: number; reserved: number;
    max_volume?: number;
    used_volume?: number;
};




const toOptions = (list: Array<{ id?: string; name?: string }> = []): SelectOption[] =>
    (Array.isArray(list) ? list : [])?.map((x) => ({ value: x?.id || x?.name || "", label: x?.name || "" }));



const conditionOptions: SelectOption[] = [
    { value: "new", label: "New" },
    { value: "used_like_new", label: "Used - Like New" },
    { value: "used_good", label: "Used - Good" },
];


const statusOptions: SelectOption[] = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
];

type PreviewFile = { file: File; url: string };


const SignageEditAgentProduct = () => {
    const { agentId } = useParams<{ agentId: string }>();
    const { productId } = useParams<{ productId: string }>();
    const [agent, setAgent] = useState<Agent | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [formSubmitting, setFormSubmitting] = useState(false);
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const [preview, setPreview] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Determine the base route based on current location
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    
    // ✅ Use the brand-category selector hook
    const {
        selectedBrand,
        setSelectedBrand,
        selectedCategories,
        setSelectedCategories,
        brandOptions: brandOptionsFromHook,
        filteredCategories: categoryOptionsFromHook,
        loadingBrands,
        loadingCategories,
        brandCategoryIds,
    } = useBrandCategorySelector(agentId || "", token || "");
    
    const [product, setProduct] = useState<Product>();
    const [productobj, setProductObj] = useState<ProductObj>();
    const [installers, setInstallers] = useState<Installer[]>([]);
    const [errormsg, setErrormsg] = useState<any>();
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);

    const [warehouseData, setWarehouseData] = useState<
        {
            warehouse_id: string;
            warehouse_name: string;
            zone_id: string;
            zone_name: string;
            rack_id: string;
            rack_name: string;
            bin_id: string;
            bin_name: string;
            available: number;
            on_hand: number;
            reserved: number;
            max_volume?: number;
            used_volume?: number;
        }[]
    >([]);
    
    // Key to force table re-render when bin data is loaded
    const [tableKey, setTableKey] = useState(0);

    const [selectedLocations, setSelectedLocations] = useState<Array<{
        quantity: number;
        warehouse_id: string; warehouse_name: string;
        zone_id: string; zone_name: string;
        rack_id: string; rack_name: string;
        bin_id: string; bin_name: string;
    }>>([]);
    const [selectedTotal, setSelectedTotal] = useState(0);


    const [warehouseErrors, setWarehouseErrors] = useState<{
        zone: boolean;
        rack: boolean;
        bin: boolean;
    }[]>([]);

    useEffect(() => {
        setWarehouseErrors(warehouseData.map(() => ({ zone: false, rack: false, bin: false })));
    }, [warehouseData]);


    // Handle Product Details quantity change - distribute to warehouse table
    const handleQuantityChange = (value: string) => {
        const newQty = Number(value) || 0;
        formik.setFieldValue("qty", newQty);

        // Distribute the new quantity across warehouses proportionally
        const currentTotal = warehouseData.reduce((sum, wh) => sum + Number(wh.available || 0), 0);

        if (currentTotal === 0) {
            // If no current distribution, put all quantity in first warehouse
            setWarehouseData(prev =>
                prev.map((wh, index) =>
                    index === 0
                        ? { ...wh, available: newQty, on_hand: newQty }
                        : { ...wh, available: 0, on_hand: 0 }
                )
            );
        } else {
            // Distribute proportionally
            setWarehouseData(prev =>
                prev.map(wh => {
                    const proportion = Number(wh.available || 0) / currentTotal;
                    const newAvailable = Math.round(newQty * proportion);
                    return {
                        ...wh,
                        available: newAvailable,
                        on_hand: newAvailable
                    };
                })
            );
        }
    };

    const validateWarehouseData = (): boolean => {
        let hasErrors = false;
        const errors = warehouseData.map(() => ({ zone: false, rack: false, bin: false }));

        warehouseData.forEach((wh, i) => {
            if (wh.available > 0) {
                // Validate that bin location is selected (which includes all hierarchy)
                if (!wh.bin_id || !wh.warehouse_id || !wh.zone_id || !wh.rack_id) {
                    errors[i].bin = true;
                    hasErrors = true;
                }
            }
        });

        setWarehouseErrors(errors);
        return !hasErrors;
    };



    // useEffect(() => {
    //     if (warehouses?.length > 0 && productobj?.linked_installer_details?.storage_locations) {
    //         // Create a map of existing storage locations for quick lookup
    //         type StorageLocationData = {
    //             zone_id: string;
    //             zone_name: string;
    //             rack_id: string;
    //             rack_name: string;
    //             bin_id: string;
    //             bin_name: string;
    //             available: number;
    //             on_hand: number;
    //             reserved: number;
    //         };
    //         const storageMap = new Map<string, StorageLocationData>(
    //             (productobj.linked_installer_details.storage_locations || []).map((loc: any) => [
    //                 loc.location.warehouse_id,
    //                 {
    //                     zone_id: loc.location.zone_id || "",
    //                     zone_name: loc.location.zone || "",
    //                     rack_id: loc.location.rack_id || "",
    //                     rack_name: loc.location.rack || "",
    //                     bin_id: loc.location.bin_id || "",
    //                     bin_name: loc.location.bin || "",
    //                     available: loc.quantity ?? 0,
    //                     on_hand: loc.quantity ?? 0,
    //                     reserved: 0,
    //                 },
    //             ])
    //         );

    //         // Map all warehouses and populate with existing data where available
    //         const fullWarehouseData = warehouses?.map((w) => {
    //             const existingData = storageMap.get(w._id);
    //             return {
    //                 warehouse_id: w._id,
    //                 warehouse_name: w.label,
    //                 zone_id: existingData?.zone_id || "",
    //                 zone_name: existingData?.zone_name || "",
    //                 rack_id: existingData?.rack_id || "",
    //                 rack_name: existingData?.rack_name || "",
    //                 bin_id: existingData?.bin_id || "",
    //                 bin_name: existingData?.bin_name || "",
    //                 available: existingData?.available ?? 0,
    //                 on_hand: existingData?.on_hand ?? 0,
    //                 reserved: existingData?.reserved ?? 0,
    //             };
    //         });
    //         setWarehouseData(fullWarehouseData);
    //     }
    // }, [warehouses, productobj]);

    // Initialize warehouseData from storage_locations and fetch current bin data from API
    useEffect(() => {
        if (productobj?.linked_installer_details?.storage_locations) {
            const result: typeof warehouseData = [];
            const binsToFetch: Array<{ bin_id: string; bin_name: string; index: number }> = [];

            productobj.linked_installer_details.storage_locations.forEach((storage: any) => {
                if (Array.isArray(storage.location)) {
                    storage.location.forEach((loc: any, idx: number) => {
                        result.push({
                            warehouse_id: loc.warehouse_id || "",
                            warehouse_name: loc.warehouse_name || "",
                            zone_id: loc.zone_id || "",
                            zone_name: loc.zone_name || "",
                            rack_id: loc.rack_id || "",
                            rack_name: loc.rack_name || "",
                            bin_id: loc.bin_id || "",
                            bin_name: loc.bin_name || "",
                            available: loc.quantity ?? storage.quantity ?? 0,
                            on_hand: loc.quantity ?? storage.quantity ?? 0,
                            reserved: 0,
                        });
                        if (loc.bin_id && loc.bin_name) {
                            binsToFetch.push({
                                bin_id: loc.bin_id,
                                bin_name: loc.bin_name,
                                index: result.length - 1,
                            });
                        }
                    });
                }
            });

            setWarehouseData(result);

            // Fetch current bin data for each bin by calling direct bin endpoint
            if (binsToFetch.length > 0 && token) {
                const fetchBinData = async () => {
                    try {
                        // Fetch bin data for each bin by calling direct bin endpoint
                        const binDataMap = new Map<string, { max_volume?: number; used_volume?: number }>();

                        // Fetch each bin directly by bin_id
                        await Promise.all(
                            binsToFetch.map(async ({ bin_id, bin_name }) => {
                                try {
                                    const url = `${process.env.REACT_APP_API_URL}/bins/${bin_id}`;
                                    const response = await fetch(url, {
                                        method: "GET",
                                        headers: {
                                            "Content-Type": "application/json",
                                            Authorization: `Bearer ${token}`,
                                        },
                                    });

                                    if (response.ok) {
                                        const binData = await response.json();
                                        
                                        // Extract max_volume and used_volume from direct bin response
                                        if (binData) {
                                            binDataMap.set(bin_id, {
                                                max_volume: binData.max_volume,
                                                used_volume: binData.used_volume,
                                            });
                                        }
                                    }
                                } catch (err) {
                                    // Silently handle fetch errors
                                }
                            })
                        );

                        // Update warehouseData with fetched bin data
                        setWarehouseData((prev) => 
                            prev.map((wh) => {
                                const binData = binDataMap.get(wh.bin_id);
                                return binData 
                                    ? { ...wh, max_volume: binData.max_volume, used_volume: binData.used_volume }
                                    : { ...wh };
                            })
                        );
                        
                        // Force table re-render after data is updated
                        setTableKey(prev => prev + 1);
                    } catch (err) {
                        console.error("Failed to fetch bin data:", err);
                    }
                };

                fetchBinData();
            }
        }
    }, [productobj, token]);






    // Fetch product data
    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/agent/${agentId}/inventory/${productId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const result: any = await response.json();
                if (!response.ok || result?.error) {
                    const message = result?.error || result?.message || AGENTOFFICEPRODUCTADDEDIT_MESSAGES.fetch.error;
                    console.error("API Error:", message);
                    messageHandler.product.error("fetch", new Error(message));
                    throw new Error(message);
                }
                setProduct(result.product);
                setProductObj(result);
                // console.log(result?.product?.images, "result?.product?.[0]?.images");

                const variantImages =
                    Array.isArray(result?.product?.variants?.[0]?.images)
                        ? result.product.variants[0].images
                        : [];
                const productImages = Array.isArray(result?.product?.images)
                    ? result.product.images
                    : [];

                const seen = new Set<string>();
                const merged = [...variantImages, ...productImages].filter((img: any) => {
                    const url = String(img?.url ?? "");
                    if (!url) return false;
                    if (seen.has(url)) return false;
                    seen.add(url);
                    return true;
                });

                setUploadedFileMeta(merged);
                setErrormsg(result.linked_installer_details.current_status)
                // console.log(result.linked_installer_details.current_status, "result.linked_installer_details.current_status");
                // console.log(errormsg, "errormsg");

                setAgent(result.created_by);
            } catch (err) {
                console.error("Error fetching category data:", err);
                messageHandler.product.error("fetch", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.category.fetchError));
            } finally {
                setLoading(false);
            }
        };

        if (productId) fetchProduct();
    }, [productId, token]);

    // ✅ Brands and categories are now loaded via useBrandCategorySelector hook above
    // No separate useEffects needed!

    // useEffect(() => {
    //     return () => {
    //         if (preview) URL.revokeObjectURL(preview);
    //     };
    // }, [preview]);

    // useEffect(() => {
    //     const fetchInstallers = async () => {
    //         try {
    //             const response = await fetch(`${process.env.REACT_APP_API_URL}/agent/suppliers`, {
    //                 method: "GET",
    //                 headers: {
    //                     Authorization: `Bearer ${token}`,
    //                     "Content-Type": "application/json",
    //                 },
    //             });

    //             if (!response.ok) {
    //                 throw new Error("Failed to fetch installers data");
    //             }

    //             const result: any = await response.json();
    //             if (!response.ok || result?.error) {
    //                 const message = result?.error || result?.message || "Failed to save data";
    //                 console.error("API Error:", message);
    //                 showError(message);
    //                 throw new Error(message);
    //             }
    //             console.log("Fetched Installers:", result);

    //             if (Array.isArray(result)) {
    //                 setInstallers(result);
    //             } else {
    //                 console.error("Unexpected installers result format:", result);
    //             }
    //         } catch (error) {
    //             console.error("Error fetching installers result:", error);
    //         }
    //     };

    //     fetchInstallers();
    // }, [token]);


    function getInstallerById(installerId: string) {
        // console.log("Looking up installer for ID:", installerId);
        return installers?.find((installer) => installer.supplier_id === installerId) || null;
    }

    // Select2 API configuration for bin locations
    const PAGE_SIZE_BIN = 20;

    // Load bin location options using Select2 API
    const loadBinLocationOptions: LoadOptions<BinLocationOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;

            try {
                const search = (inputValue || "").trim();
                const url = `${process.env.REACT_APP_API_URL}/bins/select-options`;

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        page,
                        limit: PAGE_SIZE_BIN,
                        search: search || undefined,
                    }),
                });

                if (!response.ok) {
                    console.error("Failed to fetch bin locations:", response.statusText);
                    return { options: [], hasMore: false, additional: { page } };
                }

                const json = await response.json();
                const results = Array.isArray(json.results) ? json.results : [];

                // Map API response to BinLocationOption format
                const options: BinLocationOption[] = results.map((item: any) => {
                    const label = `${item.warehouse_name || ""} > ${item.zone_name || ""} > ${item.rack_name || ""} > ${item.bin_name || ""}`.trim();
                    return {
                        value: item.bin_id || "",
                        label: label || "Unknown Location",
                        warehouse_id: item.warehouse_id || "",
                        warehouse_name: item.warehouse_name || "",
                        zone_id: item.zone_id || "",
                        zone_name: item.zone_name || "",
                        rack_id: item.rack_id || "",
                        rack_name: item.rack_name || "",
                        bin_id: item.bin_id || "",
                        bin_name: item.bin_name || "",
                        max_volume: item.max_volume,
                        used_volume: item.used_volume,
                    };
                });

                const hasMore = json.pagination?.more ?? false;

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                console.error("Failed to load bin location options:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [token]
    );

    // const uploadAllSelectedImages = async () => {
    //   const uploads = await Promise.all(
    //     previewFiles.map(async ({ file }) => ({ alt_text: file.name, url: await uploadFile(file) }))
    //   );
    //   const all = [...(uploadedFileMeta || []), ...uploads];
    //   // de-dupe by filename
    //   const uniq = all.filter((f, i, a) => i === a.findIndex(x => x.alt_text === f.alt_text));
    //   setUploadedFileMeta(uniq);
    //   setPreviewFiles([]);
    //   return uniq;
    // };


    // helper: normalize & dedupe by URL
    const sourceUrls = React.useMemo(() => {
        const fromUploads =
            Array.isArray(uploadedFileMeta)
                ? uploadedFileMeta.map((m: any) => m?.url).filter(Boolean)
                : [];

        const fromVariant =
            productobj?.product?.variants?.[0]?.images?.map((i: any) => i?.url).filter(Boolean) ?? [];

        const fromProduct =
            productobj?.product?.images?.map((i: any) => i?.url).filter(Boolean) ?? [];

        const merged = [...fromUploads, ...fromVariant, ...fromProduct];

        // ✅ De-dupe without iterating a Set (no downlevelIteration needed)
        const seen = new Set<string>();
        const unique: string[] = [];
        for (let i = 0; i < merged.length; i++) {
            const v = String(merged[i] ?? "");
            if (!v) continue;
            if (!seen.has(v)) {
                seen.add(v);
                unique.push(v);
            }
        }
        return unique;
    }, [uploadedFileMeta, productobj]);

    const imageUrlMap = useVariantImageUrlMap(sourceUrls, token);


    async function uploadProductImagesToCloudDrive(): Promise<string[]> {
        const tok = (token || "").trim() || null;
        // use the files you already collected in previewFiles
        const files = (previewFiles || []).map(p => p.file);
        if (!files.length) return [];

        const ids = await uploadFilesAndGetIds(files, tok);
        setPreviewFiles([]);           // clear after successful upload
        return ids;                    // send back to onSubmit
    }


    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: product?.name || "",
            description: product?.description || "",
            sku: product?.sku || "",
            brand: product?.brand_id || "",  // ✅ Use brand_id for value
            category: product?.category_id || "",  // ✅ Use category_id for value
            item_condition: product?.item_condition || "",
            length: product?.package_dimensions?.length || 0,
            height: product?.package_dimensions?.height || 0,
            width: product?.package_dimensions?.width || 0,
            weight: product?.package_dimensions?.weight || 0,
            qty: product?.variants?.[0]?.inventory?.[0]?.available || product?.variants?.[0]?.inventory?.[0]?.on_hand || 0,
            threshold_qty: product?.threshold_qty || 0,
            installerId: product?.linked_installer || "",
            notes: product?.notes || "",
            image: null, // Initialize as null for new uploads
            imageUrl: product?.image?.url || null, // Use this for prefilled image URL
            statuss: product?.status || "Active",
            is_rotational: product?.is_rotational ? "yes" : "no",
            linked_agent: productobj?.agent_name || "",
            installer_notes: "",
            linked_installer: product?.linked_installer || "",
            installer_status: product?.linked_mode,
        },
        validationSchema: Yup.object({
            name: nameValidation,
            sku: Yup.string().notRequired(),
            brand: Yup.string().notRequired(),
            category: Yup.string().notRequired(),
            // statuss: statusValidation,
        }),
        onSubmit: async (values) => {
            // console.log("Submitting form...", values);
            setFormSubmitting(true);
            if (!validateWarehouseData()) {
                messageHandler.product.error("update", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.validation.warehouseDetailsRequired));
                setFormSubmitting(false);
                return;
            }
            // Include every fully selected location, including quantity 0.
            // Incomplete rows are already excluded by the shared inventory UI payload.
            const filteredWarehouses = selectedLocations.map(loc => ({
                warehouse_id: loc.warehouse_id,
                warehouse_name: loc.warehouse_name,
                zone_id: loc.zone_id,
                zone_name: loc.zone_name,
                rack_id: loc.rack_id,
                rack_name: loc.rack_name,
                bin_id: loc.bin_id,
                bin_name: loc.bin_name,
                available: Number(loc.quantity ?? 0),
                on_hand: Number(loc.quantity ?? 0),
                reserved: 0,
            }));

            // 2) Sum total (can be 0 when all location quantities are 0)
            const totalAvailable = filteredWarehouses.reduce((sum, wh) => sum + Number(wh.available ?? 0), 0);

            // (Optional) If you still need inventory to reflect the total, your existing `updatedInventory` block can stay.
            // Just ensure it uses `totalAvailable` as you already do.


            // Check if at least one warehouse has available > 0
            // if (errormsg === "with_supplier") {
            //     if (filteredWarehouses.length === 0) {
            //         messageHandler.product.error("update", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.validation.quantityRequired));
            //         setFormSubmitting(false);
            //         return;
            //     }
            // }

            try {

                const newImageIds = await uploadProductImagesToCloudDrive();

                // 2) Convert *existing* (kept) images from state as-is
                const kept: Media[] = (uploadedFileMeta ?? []).map((m: any) => ({
                    url: m?.url,
                    alt_text: m?.alt_text || "",
                    default: !!m?.default,
                }));

                // 3) Convert *new* uploads into Media[]
                const newMedia: Media[] = newImageIds.map((id, idx) => ({
                    url: id,
                    alt_text: (previewFiles?.[idx]?.file?.name as string) || "",
                    default: false,
                }));

                // 4) Merge and ensure exactly one default (if none, make the first one default)
                let images: Media[] = [...kept, ...newMedia];
                if (!images.some((m) => m.default) && images.length > 0) {
                    images = images.map((m, i) => ({ ...m, default: i === 0 }));
                }

                try {
                    if (pendingDeleteIds.length > 0) {
                        await hardDeletePresignedFiles(pendingDeleteIds, token || null);
                        setPendingDeleteIds([]); // clear
                    }
                } catch (delErr: any) {
                    console.error("Image hard delete (batch) failed:", delErr);
                    // Don't block success — just inform
                    messageHandler.product.error("update", new Error(delErr?.message || AGENTOFFICEPRODUCTADDEDIT_MESSAGES.image.deleteFailed));
                }


                // 1️⃣ Get old inventory (totalAvailable already computed from all locations, including zeros)
                const oldInventory = product?.variants?.[0]?.inventory || [];

                // 3️⃣ Map old inventory, but override available with the total
                const updatedInventory = oldInventory.map(oldItem => ({
                    warehouse_id: oldItem.warehouse_id,
                    warehouse_name: oldItem.warehouse_name,
                    // zone_id: oldItem.zone_id,
                    // zone_name: oldItem.zone_name,
                    // rack_id: oldItem.rack_id,
                    // rack_name: oldItem.rack_name,
                    // bin_id: oldItem.bin_id,
                    // bin_name: oldItem.bin_name,
                    on_hand: totalAvailable,
                    reserved: oldItem.reserved,
                    available: totalAvailable, // 👈 always use the sum!
                }));

                // Find brand and category names from IDs
                const selectedBrandOption = brandOptionsMemo.find(b => b.value === values.brand);
                const selectedCategoryOption = categoryOptionsMemo.find(c => c.value === values.category);

                // 2. Submit product details
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/agent/${agentId}/inventory/${productId}`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            inventory_type: "agent",
                            product: {
                                name: values.name,
                                sku: values.sku,
                                description: values.description,
                                product_type: "simple",
                                ...(() => {
                                    const brandId = String(values.brand || "").trim();
                                    const brandName = String(selectedBrandOption?.label || "").trim();
                                    const categoryId = String(values.category || "").trim();
                                    const categoryName = String(selectedCategoryOption?.label || "").trim();
                                    const isMeaningfulId = (id: string) => Boolean(id) && !/^0+$/.test(id);
                                    return {
                                        ...(isMeaningfulId(brandId)
                                            ? { brand: brandName, brand_id: brandId }
                                            : {}),
                                        ...(isMeaningfulId(categoryId)
                                            ? { category: categoryName, category_id: categoryId }
                                            : {}),
                                    };
                                })(),
                                item_condition: values.item_condition,
                                purchase_price: product?.purchase_price,
                                retail_price: product?.retail_price,
                                package_dimensions: {
                                    length: Number(values.length),
                                    height: Number(values.height),
                                    width: Number(values.width),
                                    weight: Number(values.weight),
                                    weight_type: "g",
                                },
                                min_qty: 1,
                                status: values.statuss,
                                threshold_qty: Number(values.threshold_qty),
                                images,
                                variants: [
                                    {
                                        variant_name: product?.name,
                                        sku: product?.sku,
                                        attributes: product?.variants?.[0]?.attributes,
                                        purchase_price: product?.variants[0]?.purchase_price,
                                        retail_price: product?.variants[0]?.retail_price,
                                        images,
                                        package_dimensions: {
                                            length: Number(values.length),
                                            height: Number(values.height),
                                            width: Number(values.width),
                                            weight: Number(values.weight),
                                            weight_type: "g",
                                        },
                                        inventory: updatedInventory,
                                        // inventory: [
                                        // {
                                        //     // warehouse_id: "",
                                        //     // warehouse_name: "",
                                        //     on_hand: Number(values.qty),
                                        //     available: Number(values.qty),
                                        //     reserved: 0,
                                        // },
                                        // ],
                                    },
                                ],
                                linked_mode: product?.linked_mode,
                                locked_status: product?.locked_status,
                                linked_installer: product?.linked_installer,
                                product_location_status: product?.product_location_status,
                                production_status: product?.production_status,
                                is_production: product?.is_production,
                                is_rotational: product?.is_rotational,
                                current_status: productobj?.linked_installer_details.current_status,

                            },
                            linked_installer: {
                                product_location_status: product?.product_location_status,
                                current_status: productobj?.linked_installer_details.current_status,
                                storage_locations: [
                                    {
                                        quantity: totalAvailable,
                                        assigned_at: new Date().toISOString(),
                                        product_location_status: product?.product_location_status,
                                        current_status: productobj?.linked_installer_details.current_status,
                                        location: filteredWarehouses.map((wh) => ({
                                            quantity: wh.available,
                                            warehouse_id: wh.warehouse_id,
                                            warehouse_name: wh.warehouse_name,
                                            zone_id: wh.zone_id,
                                            zone_name: wh.zone_name,
                                            rack_id: wh.rack_id,
                                            rack_name: wh.rack_name,
                                            bin_id: wh.bin_id,
                                            bin_name: wh.bin_name,
                                        })),
                                    },
                                ],
                            }

                        }),
                    }
                );

                const result: any = await response.json();
                if (!response.ok || result?.error) {
                    const message = result?.error || result?.message || AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.updateFailed;
                    console.error("API Error:", message);
                    messageHandler.product.error("update", new Error(message));
                    throw new Error(message);
                }
                messageHandler.product.success("update");
                navigate(-1);
                // if (!response.ok) {
                //     toast.error(result);
                // }
                // else {
                //     navigate(-1)
                // }
            } catch (err) {
                console.error(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setFormSubmitting(false);
            }
        },
    });

    // Sync Product Details quantity with warehouse table total
    // DISABLED: This was preventing manual quantity changes
    // useEffect(() => {
    //     const totalAvailable = warehouseData.reduce((sum, wh) => sum + Number(wh.available || 0), 0);
    //     if (formik.values.qty !== totalAvailable) {
    //         formik.setFieldValue("qty", totalAvailable);
    //     }
    // }, [warehouseData, formik]);

    // Pass this as the "saving" flag to the shared UI
    const saving = formSubmitting;


    const toSO = (list: any[] = []) =>
        (Array.isArray(list) ? list : []).map((x) => ({ value: String(x._id ?? x.id ?? ""), label: String(x.label ?? x.name ?? "") }));

    // Local input component to prevent cursor jumping
    const QuantityInput = ({ value, onUpdate }: { value: number; onUpdate: (n: number) => void }) => {
        const [localValue, setLocalValue] = React.useState(String(value ?? 0));

        React.useEffect(() => {
            setLocalValue(String(value ?? 0));
        }, [value]);

        return (
            <input
                type="text"
                className="form-control text-center"
                value={localValue}
                placeholder="0"
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() => {
                    const n = Number(localValue) || 0;
                    onUpdate(n);
                }}
                style={{ minWidth: '80px' }}
            />
        );
    };

    // Create augmented storage locations with volume data for the child component
    const [augmentedStorageLocations, setAugmentedStorageLocations] = useState<any>(null);

    // Optimize: Only update when warehouseData has volume data
    useEffect(() => {
        const hasVolumeData = warehouseData.some(wh => wh.max_volume !== undefined && wh.max_volume > 0);
        
        if (hasVolumeData && productobj?.linked_installer_details?.storage_locations) {
            const augmented = productobj.linked_installer_details.storage_locations.map((storage: any) => ({
                ...storage,
                location: storage.location.map((loc: any) => {
                    const matchedWh = warehouseData.find(wh => wh.bin_id === loc.bin_id);
                    return {
                        ...loc,
                        max_volume: matchedWh?.max_volume,
                        used_volume: matchedWh?.used_volume,
                    };
                }),
            }));
            setAugmentedStorageLocations(augmented);
        }
    }, [warehouseData, productobj]);

    const columns: ColumnDef<WHRow>[] = useMemo(() => [
        {
            accessorKey: "warehouse_name",
            header: "Warehouse",
            cell: ({ row }) => <div className="">{row.original.warehouse_name}</div>,
        },
        {
            id: "available",
            header: "Quantity",
            cell: ({ row }) => {
                const wh = row.original;
                return (
                    <div className="">
                        <QuantityInput
                            value={wh.available}
                            onUpdate={(n) => {
                                setWarehouseData((prev) =>
                                    prev.map((r, i) =>
                                        i === row.index
                                            ? {
                                                ...r,
                                                available: n,
                                                on_hand: n,
                                                // clear dependent selections when qty is zero
                                                zone_id: n === 0 ? "" : r.zone_id,
                                                zone_name: n === 0 ? "" : r.zone_name,
                                                rack_id: n === 0 ? "" : r.rack_id,
                                                rack_name: n === 0 ? "" : r.rack_name,
                                                bin_id: n === 0 ? "" : r.bin_id,
                                                bin_name: n === 0 ? "" : r.bin_name,
                                            }
                                            : r
                                    )
                                );
                            }}
                        />
                    </div>
                );
            },
        },
        {
            id: "bin_location",
            header: "Bin Location",
            cell: ({ row }) => {
                const wh = row.original;
                const disabled = !(wh.available > 0);

                // Get current selected bin location option
                const currentOption: BinLocationOption | null = wh.bin_id
                    ? {
                        value: wh.bin_id,
                        label: `${wh.warehouse_name || ""} > ${wh.zone_name || ""} > ${wh.rack_name || ""} > ${wh.bin_name || ""}`.trim() || "Selected Location",
                        warehouse_id: wh.warehouse_id,
                        warehouse_name: wh.warehouse_name,
                        zone_id: wh.zone_id,
                        zone_name: wh.zone_name,
                        rack_id: wh.rack_id,
                        rack_name: wh.rack_name,
                        bin_id: wh.bin_id,
                        bin_name: wh.bin_name,
                        max_volume: wh.max_volume,
                        used_volume: wh.used_volume,
                    }
                    : null;

                return (
                    <div style={{ minWidth: "250px" }}>
                        <AsyncPaginate
                            classNamePrefix="rs"
                            placeholder="Search bin location..."
                            isDisabled={disabled}
                            value={currentOption}
                            loadOptions={loadBinLocationOptions}
                            onChange={(selected: BinLocationOption | null) => {
                                if (selected) {
                                    setWarehouseData((prev) =>
                                        prev.map((r, i) =>
                                            i === row.index
                                                ? {
                                                    ...r,
                                                    warehouse_id: selected.warehouse_id,
                                                    warehouse_name: selected.warehouse_name,
                                                    zone_id: selected.zone_id,
                                                    zone_name: selected.zone_name,
                                                    rack_id: selected.rack_id,
                                                    rack_name: selected.rack_name,
                                                    bin_id: selected.bin_id,
                                                    bin_name: selected.bin_name,
                                                    max_volume: selected.max_volume,
                                                    used_volume: selected.used_volume,
                                                }
                                                : r
                                        )
                                    );
                                } else {
                                    // Clear location when deselected
                                    setWarehouseData((prev) =>
                                        prev.map((r, i) =>
                                            i === row.index
                                                ? {
                                                    ...r,
                                                    warehouse_id: "",
                                                    warehouse_name: "",
                                                    zone_id: "",
                                                    zone_name: "",
                                                    rack_id: "",
                                                    rack_name: "",
                                                    bin_id: "",
                                                    bin_name: "",
                                                    max_volume: undefined,
                                                    used_volume: undefined,
                                                }
                                                : r
                                        )
                                    );
                                }
                            }}
                            additional={{ page: 1 }}
                            defaultOptions
                            isClearable
                            styles={{
                                control: (provided: any, state: any) => ({
                                    ...provided,
                                    minHeight: '38px',
                                    border: state.isFocused ? '1px solid #86b7fe' : '1px solid #ced4da',
                                    borderRadius: '0.375rem',
                                    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                                    '&:hover': {
                                        borderColor: '#86b7fe'
                                    }
                                }),
                                placeholder: (provided: any) => ({
                                    ...provided,
                                    color: '#6c757d',
                                }),
                                menu: (provided: any) => ({
                                    ...provided,
                                    zIndex: 9999,
                                }),
                            }}
                        />
                    </div>
                );
            },
        },
        {
            id: "available_quantity",
            header: "Available Quantity",
            cell: ({ row }) => {
                const wh = row.original;
                if (wh.max_volume !== undefined && wh.used_volume !== undefined) {
                    const availableQty = (wh.max_volume || 0) - (wh.used_volume || 0);
                    return <div className="">{availableQty}</div>;
                }
                return <div className="">-</div>;
            },
        },
        {
            id: "maximum_quantity",
            header: "Maximum Quantity",
            cell: ({ row }) => {
                const wh = row.original;
                if (wh.max_volume !== undefined) {
                    return <div className="">{wh.max_volume || 0}</div>;
                }
                return <div className="">-</div>;
            },
        },
    ], [loadBinLocationOptions]);
    
    const table: Table<WHRow> = useReactTable<WHRow>({
        data: warehouseData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
        // Force table to recognize data changes
        state: {
            columnVisibility: {},
        },
        // Add key to track data updates
        meta: { tableKey },
    });

    // ✅ Brand options from hook (with product's current brand included if not in list)
    const brandOptionsMemo = useMemo(() => {
        const brandOptions = brandOptionsFromHook;

        // Check if product's brand exists in the list
        const productBrandId = product?.brand_id;
        const productBrandName = product?.brand;

        if (productBrandId && productBrandName) {
            const brandExists = brandOptions.some(opt => opt.value === productBrandId);

            // If product's brand doesn't exist in the list, add it at the beginning
            if (!brandExists) {
                return [
                    { value: productBrandId, label: productBrandName },
                    ...brandOptions
                ];
            }
        }

        return brandOptions;
    }, [brandOptionsFromHook, product]);

    // ✅ Category options from hook (filtered by selected brand, with product's current category included if not in list)
    const categoryOptionsMemo = useMemo(() => {
        // ✅ Only return categories if brand is selected
        const categoryOptions = selectedBrand ? categoryOptionsFromHook : [];

        // Check if product's category exists in the list
        const productCategoryId = product?.category_id;
        const productCategoryName = product?.category;

        if (productCategoryId && productCategoryName) {
            const categoryExists = categoryOptions.some(opt => opt.value === productCategoryId);

            // If product's category doesn't exist in the list, add it at the beginning
            if (!categoryExists) {
                return [
                    { value: productCategoryId, label: productCategoryName },
                    ...categoryOptions
                ];
            }
        }

        return categoryOptions;
    }, [categoryOptionsFromHook, selectedBrand, product]);

    const PAGE_SIZE = 20;
    const effectiveUserId = agentId || "";
    type SelectOptionType = { value: string; label: string };
    type AdditionalType = { page?: number };
    const loadBrandOptions: LoadOptions<SelectOptionType, GroupBase<SelectOptionType>, AdditionalType> = useCallback(
        async (inputValue, _, { page } = { page: 1 }) => {
            const currentPage = page ?? 1;
            const resp = await fetchBrandsByUserSelect2(effectiveUserId, token || "", {
                q: inputValue || "",
                page: currentPage,
                limit: PAGE_SIZE,
            });
            const options = (resp?.results || []).map((r: { id?: string; _id?: string; text?: string; name?: string }) => ({
                value: r.id || r._id || "",
                label: r.text || r.name || "",
            }));
            const hasMore = !!resp?.pagination?.more;
            return { options, hasMore, additional: { page: currentPage + 1 } };
        },
        [effectiveUserId, token]
    );
    const loadCategoryOptions: LoadOptions<SelectOptionType, GroupBase<SelectOptionType>, AdditionalType> = useCallback(
        async (inputValue, _, { page } = { page: 1 }) => {
            const currentPage = page ?? 1;
            const resp = await fetchCategoriesByUserSelect2(effectiveUserId, token || "", {
                q: inputValue || "",
                page: currentPage,
                limit: PAGE_SIZE,
            });
            let options = (resp?.results || []).map((r: { id?: string; _id?: string; text?: string; name?: string }) => ({
                value: r.id || r._id || "",
                label: r.text || r.name || "",
            }));
            if (brandCategoryIds?.length) {
                options = options.filter((o) => brandCategoryIds.includes(o.value));
            }
            const hasMore = !!resp?.pagination?.more;
            return { options, hasMore, additional: { page: currentPage + 1 } };
        },
        [effectiveUserId, token, brandCategoryIds]
    );
    const selectedBrandOptionForAsync = brandOptionsMemo.find(b => b.value === (formik.values.brand || selectedBrand)) ?? null;
    const selectedCategoryOptionForAsync = categoryOptionsMemo.find(c => c.value === (formik.values.category || selectedCategories[0])) ?? null;

    // --- preview/add/remove handlers (simple & safe) ---
    const handleAddFiles = (files: File[]) => {
        if (!files?.length) return;
        const fresh = files.map(file => ({ file, url: URL.createObjectURL(file) }));
        setPreviewFiles(prev => [...prev, ...fresh]);
    };

    const handleRemovePreview = (idx: number) => {
        setPreviewFiles(prev => {
            const copy = [...prev];
            // revoke old blob URL to avoid leaks
            try { URL.revokeObjectURL(copy[idx]?.url); } catch { }
            copy.splice(idx, 1);
            return copy;
        });
    };

    const handleRemoveUploaded = (idx: number) => {
        const id = String(uploadedFileMeta?.[idx]?.url || "");
        if (!id) return;
        // queue for delete on save
        setPendingDeleteIds(prev => (prev.includes(id) ? prev : [...prev, id]));
        // update UI now
        setUploadedFileMeta(prev => prev.filter((_, i) => i !== idx));
    };

    // --- pass a strict string|null resolver (never undefined) ---
    const resolveImageSrc = useCallback(
        (url: string): string | null => {
            if (!url) return null;
            const resolved = getImageSrc(url, imageUrlMap);
            if (resolved) return resolved;
            if (url.startsWith("http")) return url;
            return buildLegacyUrl(url) ?? null;
        },
        [imageUrlMap]
    );

    // ✅ Refs to prevent circular updates and track previous values
    const isSyncingFromFormik = useRef(false);
    const isSyncingFromHook = useRef(false);
    const prevProductBrandId = useRef<string>("");
    const prevProductCategoryId = useRef<string>("");
    const prevFormikBrand = useRef<string>("");
    const prevFormikCategory = useRef<string>("");
    const prevSelectedBrand = useRef<string>("");
    const prevSelectedCategory = useRef<string>("");

    // ✅ Sync product's brand/category to hook when product loads (Edit mode) - Only once
    useEffect(() => {
        const productBrandId = product?.brand_id || "";
        if (productBrandId && productBrandId !== prevProductBrandId.current && productBrandId !== selectedBrand) {
            prevProductBrandId.current = productBrandId;
            // console.log("🔄 [Product→Hook] Setting brand from product:", productBrandId);
            setSelectedBrand(productBrandId);
        }
    }, [product?.brand_id, selectedBrand]);

    useEffect(() => {
        const productCategoryId = product?.category_id || "";
        if (productCategoryId && productCategoryId !== prevProductCategoryId.current) {
            const currentFirst = selectedCategories[0] || "";
            if (productCategoryId !== currentFirst) {
                prevProductCategoryId.current = productCategoryId;
                // console.log("🔄 [Product→Hook] Setting category from product:", productCategoryId);
                setSelectedCategories([productCategoryId]);
            }
        }
    }, [product?.category_id, selectedCategories]);

    // ✅ Monitor formik brand changes and sync to hook (Formik → Hook)
    useEffect(() => {
        if (isSyncingFromHook.current) {
            isSyncingFromHook.current = false;
            return;
        }

        const brandValue = formik.values.brand || "";
        if (brandValue && brandValue !== prevFormikBrand.current && brandValue !== selectedBrand) {
            prevFormikBrand.current = brandValue;
            // console.log("🔄 [Formik→Hook] Brand changed:", brandValue);
            isSyncingFromFormik.current = true;
            setSelectedBrand(brandValue);
            setSelectedCategories([]); // Clear categories when brand changes
            setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
        }
    }, [formik.values.brand, selectedBrand]);

    // ✅ Monitor formik category changes and sync to hook (Formik → Hook)
    useEffect(() => {
        if (isSyncingFromHook.current) {
            isSyncingFromHook.current = false;
            return;
        }

        const categoryValue = formik.values.category || "";
        const currentFirst = selectedCategories[0] || "";
        if (categoryValue && categoryValue !== prevFormikCategory.current && categoryValue !== currentFirst) {
            prevFormikCategory.current = categoryValue;
            // console.log("🔄 [Formik→Hook] Category changed:", categoryValue);
            isSyncingFromFormik.current = true;
            setSelectedCategories([categoryValue]);
            setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
        }
    }, [formik.values.category, selectedCategories]);

    // ✅ Monitor hook brand changes and sync to formik (Hook → Formik)
    useEffect(() => {
        if (isSyncingFromFormik.current) {
            return;
        }

        if (selectedBrand && selectedBrand !== prevSelectedBrand.current && formik.values.brand !== selectedBrand) {
            prevSelectedBrand.current = selectedBrand;
            // console.log("🔄 [Hook→Formik] Brand changed:", selectedBrand);
            isSyncingFromHook.current = true;
            formik.setFieldValue("brand", selectedBrand, false);
            setTimeout(() => { isSyncingFromHook.current = false; }, 0);
        }
    }, [selectedBrand, formik.values.brand]);

    // ✅ Monitor hook category changes and sync to formik (Hook → Formik)
    useEffect(() => {
        if (isSyncingFromFormik.current) {
            return;
        }

        const firstCategory = selectedCategories[0] || "";
        if (firstCategory && firstCategory !== prevSelectedCategory.current && formik.values.category !== firstCategory) {
            prevSelectedCategory.current = firstCategory;
            // console.log("🔄 [Hook→Formik] Category changed:", firstCategory);
            isSyncingFromHook.current = true;
            formik.setFieldValue("category", firstCategory, false);
            setTimeout(() => { isSyncingFromHook.current = false; }, 0);
        } else if (!firstCategory && formik.values.category) {
            // Clear category if hook has no selection
            if (prevSelectedCategory.current !== "") {
                prevSelectedCategory.current = "";
                // console.log("🔄 [Hook→Formik] Clearing category");
                isSyncingFromHook.current = true;
                formik.setFieldValue("category", "", false);
                setTimeout(() => { isSyncingFromHook.current = false; }, 0);
            }
        }
    }, [selectedCategories, formik.values.category]);


    if (loading) return <div className="content container"><div className="card p-4">Loading…</div></div>;
    //   if (!isLoaded) return <div>Loading...</div>;
    return (
        <SharedEditProductUI
            title="Edit Product"
            formik={formik}
            brandOptions={brandOptionsMemo}
            categoryOptions={categoryOptionsMemo}
            loadBrandOptions={loadBrandOptions}
            loadCategoryOptions={loadCategoryOptions}
            selectedBrandOption={selectedBrandOptionForAsync}
            selectedCategoryOption={selectedCategoryOptionForAsync}
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            setSelectedCategories={setSelectedCategories}
            conditionOptions={conditionOptions}
            statusOptions={statusOptions}
            saving={saving}
            onBack={() => navigate(-1)}
            onSubmit={() => formik.handleSubmit()}
            previewFiles={previewFiles}
            uploadedFiles={uploadedFileMeta}
            onAddFiles={handleAddFiles}
            onRemovePreview={handleRemovePreview}
            onRemoveUploaded={handleRemoveUploaded}
            baseUrl={process.env.REACT_APP_BASE_URL}
            warehouseTable={table}
            mobileCardIds={{ idColId: "warehouse_name", titleColId: "warehouse_name", actionColId: "actions" }}
            onQuantityChange={handleQuantityChange}
            disableQuantityField={true}  // ✅ Disable only the quantity field
            requireBrandCategorySku={false}
            resolveSrc={resolveImageSrc}
            initialStorageLocations={augmentedStorageLocations || productobj?.linked_installer_details?.storage_locations}
            onLocationsChange={(locs, total) => {
                setSelectedLocations(locs);
                setSelectedTotal(total);
                // DISABLED: This was overriding manual quantity changes
                // Only sync if user is changing inventory locations, not the quantity field
                // if (formik.values.qty !== total) formik.setFieldValue("qty", total);
            }}
            type="add"
        />
    );


};

export default SignageEditAgentProduct;
